import styled, { CSSObject } from "styled-components";
// utils
import {
    COINES_AND_STARS,
    FULL_RAY_BACKGROUND,
    STRIP_HEADER,
    ZIGZAG_PATTERN,
} from "@super-ui/components/m2c-swiggy-one-halfcard/constants";
import { getBackgroundImage } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";
// deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types.internal";

const flexCenter = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
};

const LOTTIE_CONTAINER_TOP_OFFSET = "-18px";

export const HeaderContainer = styled.div<{ isPopup: boolean }>(({ isPopup, theme }) => {
    if (isPopup) {
        return {
            ...flexCenter,
            padding: `${theme.spacing.xxSmall} ${theme.spacing.small}`,
            margin: "auto",
            width: "fit-content",
            minWidth: "63%",
            maxWidth: "85%",
            backgroundImage: getBackgroundImage(STRIP_HEADER, { width: 410, height: 56 }),
            backgroundSize: "cover",
            backgroundPositionY: "bottom",
            backgroundPositionX: "center",
            backgroundRepeat: "no-repeat",
        };
    }
    return {
        ...flexCenter,
        height: "88px",
        backgroundImage: getBackgroundImage(ZIGZAG_PATTERN, { width: 780, height: 219 }),
        backgroundSize: "cover",
        backgroundPositionY: "bottom",
    };
});

export const HeaderText = styled(Text)<{ isPopup: boolean }>(({ isPopup, theme }) => ({
    width: isPopup ? "fit-content" : "200px",
    textAlign: "center",
    marginLeft: theme.spacing.xxSmall,
    marginRight: theme.spacing.xxSmall,
}));

export const SparklingStars = styled.div<{ isPopup: boolean; leftPlaced?: boolean }>(
    ({ isPopup, leftPlaced = true, theme }) => {
        const dimension = isPopup ? theme.spacing.medium : theme.spacing.xxLarge;
        return {
            width: dimension,
            height: dimension,
            transform: leftPlaced ? "rotate(90deg)" : "rotate(0deg)",
            overflow: "hidden",
            margin: `auto ${theme.spacing.xxSmall}`,
        };
    },
);

export const InfoContainer = styled.div<{ isPopup: boolean }>(({ isPopup, theme }) => ({
    minHeight: isPopup ? "280px" : "300px",
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing.medium,
    paddingTop: `calc(2 * ${theme.spacing.xxLarge})`,
    paddingBottom: `calc(3 * ${theme.spacing.xLarge})`,

    "::before": {
        content: '""',
        position: "absolute",
        top: "40%",
        left: "50%",
        width: "120%",
        height: "120%",
        backgroundImage: getBackgroundImage(FULL_RAY_BACKGROUND, { width: 500, height: 500 }),
        backgroundSize: "cover",
        backgroundPositionY: "center",
        backgroundPositionX: "center",
        animation: "rotate 10s infinite cubic-bezier(0.25, 0.46, 0.45, 0.94)",

        "@keyframes rotate": {
            "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
            "25%": { transform: "translate(-50%, -50%) rotate(90deg)" },
            "50%": { transform: "translate(-50%, -50%) rotate(180deg)" },
            "75%": { transform: "translate(-50%, -50%) rotate(270deg)" },
            "100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
        },
    },
}));

export const InfoContent = styled.div<{ isPopup: boolean }>(({ isPopup, theme }) => ({
    width: "90%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column" as CSSObject["flexDirection"],
    textAlign: "center" as CSSObject["textAlign"],
    position: "absolute" as CSSObject["position"],
    top: "45%",
    transform: "translateY(-50%)",
    paddingTop: isPopup ? theme.spacing.xLarge : 0,
}));

export const LogoContainer = styled.div(() => ({
    width: "68.5vw",
    height: "30vw",
    backgroundImage: getBackgroundImage(COINES_AND_STARS),
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    position: "relative" as CSSObject["position"],
    animation: "popUp 0.4s forwards linear",

    "@keyframes popUp": {
        "0%": { transform: "scale(0)" },
        "80%": { transform: "scale(1.2)" },
        "100%": { transform: "scale(1)" },
    },
}));

export const Logo = styled.div<{ isOneLite?: boolean }>(({ isOneLite, theme }) => ({
    width: isOneLite ? "60%" : "50%",
    position: "absolute",
    bottom: theme.spacing?.[isOneLite ? "xLarge" : "large"],
    left: "50%",
    animation: "scaleUp 0.4s forwards linear",

    "@keyframes scaleUp": {
        "0%": { transform: "translateX(-50%) scale(0)" },
        "80%": { transform: "translateX(-50%) scale(1.2)" },
        "100%": { transform: "translateX(-50%) scale(1)" },
    },
}));

export const SubTitlesContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
}));

export const CtaContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xSmall,
    width: "100%",
    height: "48px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

export const ProgressBarWrapper = styled.div<{ withLottie?: boolean; isPopup?: boolean }>(
    ({ theme, withLottie, isPopup = false }) => ({
        width: "100%",
        height: "25px",
        borderRadius: `0 0 ${theme.cornerRadius.xLarge}  ${theme.cornerRadius.xLarge}`,
        position: "relative",
        overflow: "hidden",
        marginTop: `calc(${theme.spacing.medium} * -1)`,
        paddingBottom: isPopup ? theme.spacing.medium : `calc(var(--sab, 0px) + ${theme.spacing.medium})`,
        ...(withLottie && {
            marginTop: `calc(${theme.spacing.large} * -2)`,
        }),
    }),
);

const progressBarCommonStyles = (theme: ITheme) => ({
    height: theme.spacing.xSmall,
    borderRadius: theme.cornerRadius.xLarge,
    overflow: "hidden",
    left: "50%",
    transform: "translateX(-50%)",
});

export const ProgressBarContainer = styled.div<{ isPopup: boolean }>(({ isPopup, theme }) => {
    const commonStyles = progressBarCommonStyles(theme);
    if (isPopup) {
        return {
            position: "absolute" as CSSObject["position"],
            bottom: 0,
            width: "100%",
            ...commonStyles,
        };
    }
    return {
        position: "fixed" as CSSObject["position"],
        bottom: `calc(${theme.spacing.large} + var(--sab, 0px))`,
        width: "90%",
        background: theme.colors[theme.colorMode].primary_Sub,
        ...commonStyles,
    };
});

export const Progress = styled.div<{ duration: number; isPopup: boolean }>(({ duration, isPopup, theme }) => ({
    width: "100%",
    height: theme.spacing.xSmall,
    background: theme.colors[theme.colorMode].primary,
    animation: "progressAnimation forwards linear",
    animationDuration: `${duration}s`,
    borderRadius: isPopup ? theme.cornerRadius.small : 0,

    "@keyframes progressAnimation": {
        "0%": { width: "0px" },
        "100%": { width: "100%" },
    },
}));

export const LottieContainer = styled.div(({ theme }) => ({
    width: "100%",
    height: `calc(24 * ${theme.spacing.small})`,
    position: "relative",
    top: LOTTIE_CONTAINER_TOP_OFFSET,
}));

export const LottiePlayerContainer = styled.div({
    height: "100%",
});
